import dayjs from 'dayjs';

const formatName = (string) => {
    if (!string) return '';
    if (string === 'dps') return 'DPS';
    return string
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Convert English numbers to Bengali numbers
const englishToBengaliNumber = (englishNum) => {
    if (englishNum === null || englishNum === undefined || englishNum === '') return '';
    const bengaliNumbers = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
    return String(englishNum).replace(/\d/g, (digit) => bengaliNumbers[digit]);
};

// Convert Bengali numbers to English numbers
const bengaliToEnglishNumber = (bengaliNum) => {
    if (bengaliNum === null || bengaliNum === undefined || bengaliNum === '') return '';

    // Correct Bengali digit mapping (based on englishToBengaliNumber array)
    const bengaliToEnglishMap = {
        '০': '0',
        '১': '1',
        '২': '2',
        '৩': '3',
        '৪': '4',
        '৫': '5',
        '৬': '6',
        '৭': '7',
        '৮': '8',
        '৯': '9',
    };

    // Replace all bengali digits
    let result = String(bengaliNum);
    Object.keys(bengaliToEnglishMap).forEach((bengaliDigit) => {
        result = result.split(bengaliDigit).join(bengaliToEnglishMap[bengaliDigit]);
    });

    return result;
};

// Parse amount from formatted string (English or Bengali) back to real number
// Used for database storage where Bengali numbers need to be converted back to English
const parseAmount = (formattedAmount) => {
    if (formattedAmount === null || formattedAmount === undefined || formattedAmount === '') {
        return null;
    }

    // Convert Bengali numbers to English if present
    let englishAmount = bengaliToEnglishNumber(String(formattedAmount));

    // Remove commas and any whitespace
    englishAmount = englishAmount.replace(/,/g, '').trim();

    // Convert to number
    const numericValue = parseFloat(englishAmount);

    // Return null if not a valid number
    return isNaN(numericValue) ? null : numericValue;
};

// Main amount display function that supports language
const amountDisplay = (amount, language = 'en') => {
    if (amount === null || amount === undefined) return '...';

    const formattedAmount = new Intl.NumberFormat('en-IN').format(amount);

    // If language is Bengali, convert numbers to Bengali
    if (language === 'bn') {
        return englishToBengaliNumber(formattedAmount);
    }

    return formattedAmount;
};

// Format a raw (unsigned) amount input string for display while the user is typing.
// Unlike amountDisplay (which round-trips through parseFloat and loses trailing/insignificant
// decimal digits like the "0" in "1.04" -> "1.4" or "1.0" -> "1"), this preserves the decimal
// part exactly as typed, only applying thousands-separator formatting to the integer part.
const formatAmountInput = (rawAmount, language = 'en') => {
    if (rawAmount === null || rawAmount === undefined) return '';

    const value = String(rawAmount);
    if (value === '' || value === '.') return '';

    const dotIndex = value.indexOf('.');
    const integerPart = dotIndex === -1 ? value : value.slice(0, dotIndex);
    const decimalPart = dotIndex === -1 ? null : value.slice(dotIndex + 1);

    const integerNumeric = integerPart === '' ? 0 : parseInt(integerPart, 10);
    if (isNaN(integerNumeric)) return '';

    let displayValue = amountDisplay(integerNumeric, language);

    if (decimalPart !== null) {
        const decimalDisplay =
            language === 'bn' ? englishToBengaliNumber(decimalPart) : decimalPart;
        displayValue += `.${decimalDisplay}`;
    }

    return displayValue;
};

// Main date display function that supports language
const dateDisplay = (date, language = 'en', format = 'long') => {
    if (!date) return '...';

    try {
        const dateObj = dayjs(date);
        if (!dateObj.isValid()) return '....';

        // Bengali months
        const bengaliMonths = {
            january: 'জানুয়ারি',
            february: 'ফেব্রুয়ারি',
            march: 'মার্চ',
            april: 'এপ্রিল',
            may: 'মে',
            june: 'জুন',
            july: 'জুলাই',
            august: 'আগস্ট',
            september: 'সেপ্টেম্বর',
            october: 'অক্টোবর',
            november: 'নভেম্বর',
            december: 'ডিসেম্বর',
        };

        // English months
        const englishMonths = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];

        const day = dateObj.date();
        const month = dateObj.month(); // 0-11
        const year = dateObj.year();

        let formattedDate;

        if (format === 'short') {
            // Format: 1 Feb 2026 or 1 ফেব 2026
            const monthShort =
                language === 'bn'
                    ? bengaliMonths[Object.keys(bengaliMonths)[month]].slice(0, 3)
                    : englishMonths[month].slice(0, 3);

            const dayNum = language === 'bn' ? englishToBengaliNumber(day) : day;
            const yearNum = language === 'bn' ? englishToBengaliNumber(year) : year;

            formattedDate = `${dayNum} ${monthShort} ${yearNum}`;
        } else {
            // Format: 1 February 2026 or 1 ফেব্রুয়ারি 2026 (default long format)
            const monthFull =
                language === 'bn'
                    ? bengaliMonths[Object.keys(bengaliMonths)[month]]
                    : englishMonths[month];

            const dayNum = language === 'bn' ? englishToBengaliNumber(day) : day;
            const yearNum = language === 'bn' ? englishToBengaliNumber(year) : year;

            if (format === 'monthOnly') {
                if (dateObj.year() === new Date().getFullYear()) {
                    formattedDate = `${monthFull}`;
                } else {
                    formattedDate = `${monthFull} ${yearNum}`;
                }
            } else if (format === 'numericDate') {
                const monthNum = month + 1;
                const paddedMonth = monthNum < 10 ? `0${monthNum}` : monthNum;
                const monthDisplay =
                    language === 'bn' ? englishToBengaliNumber(paddedMonth) : paddedMonth;
                formattedDate = `${dayNum}-${monthDisplay}-${yearNum}`;
            } else if (format === 'monthYear') {
                const monthNum = month + 1;
                const paddedMonth = monthNum < 10 ? `0${monthNum}` : monthNum;
                const monthDisplay =
                    language === 'bn' ? englishToBengaliNumber(paddedMonth) : paddedMonth;
                formattedDate = `${monthDisplay}/${yearNum}`;
            } else {
                formattedDate = `${dayNum} ${monthFull} ${yearNum}`;
            }
        }

        return formattedDate;
    } catch (error) {
        return '...';
    }
};

const getDateTimeInTimezone = () => {
    const now = new Date();

    // Use Intl.DateTimeFormat to handle timezone conversion
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: getUserTimezone(),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const formattedDate = `${parts.find((p) => p.type === 'year').value}-${parts.find((p) => p.type === 'month').value}-${parts.find((p) => p.type === 'day').value}`;
    const formattedTime = `${parts.find((p) => p.type === 'hour').value}:${parts.find((p) => p.type === 'minute').value}`;

    return `${formattedDate}T${formattedTime}`;
};

function normalizeMonthParam(monthStr) {
    const parsed = dayjs(monthStr, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) {
        return dayjs().startOf('month').format('YYYY-MM-01');
    }
    return parsed.startOf('month').format('YYYY-MM-01');
}

function getUserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function isDarkModeOn() {
    return localStorage.getItem('darkMode') === 'true';
}

/**
 * Get translated month name
 * @param {string} monthShortCode - 3-letter month code (e.g., 'Jan', 'Feb')
 * @param {function} t - Translation function from useLanguage hook
 * @returns {string} Translated month name
 */
const getMonthName = (monthShortCode, t) => {
    if (!monthShortCode || !t) return monthShortCode;

    const monthMap = {
        Jan: 'january',
        Feb: 'february',
        Mar: 'march',
        Apr: 'april',
        May: 'may',
        Jun: 'june',
        Jul: 'july',
        Aug: 'august',
        Sep: 'september',
        Oct: 'october',
        Nov: 'november',
        Dec: 'december',
    };

    const key = monthMap[monthShortCode];
    return key ? t(key) : monthShortCode;
};

const numberLanguageWise = (number, language) => {
    if (language === 'bn') {
        return englishToBengaliNumber(number);
    }
    return number;
};

const shortMonthConverter = (month, language) => {
    const monthMap = {
        Jan: 'জানু',
        Feb: 'ফেব',
        Mar: 'মার্চ',
        Apr: 'এপ্রিল',
        May: 'মে',
        Jun: 'জুন',
        Jul: 'জুলাই',
        Aug: 'আগস্ট',
        Sep: 'সেপ্টে',
        Oct: 'অক্টো',
        Nov: 'নভে',
        Dec: 'ডিসে',
    };
    const key = monthMap[month];
    return language === 'bn' ? key : month;
};

// Levenshtein distance - calculate minimum edits needed to transform one string to another
const levenshteinDistance = (str1, str2) => {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    if (s1.length === 0) return s2.length;
    if (s2.length === 0) return s1.length;

    const matrix = Array(s2.length + 1)
        .fill(null)
        .map(() => Array(s1.length + 1).fill(0));

    for (let i = 0; i <= s1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= s2.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= s2.length; j++) {
        for (let i = 1; i <= s1.length; i++) {
            const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
            matrix[j][i] = Math.min(
                matrix[j][i - 1] + 1,
                matrix[j - 1][i] + 1,
                matrix[j - 1][i - 1] + indicator
            );
        }
    }

    return matrix[s2.length][s1.length];
};

// Calculate similarity score (0-100) between two strings
// 100 = identical, 0 = completely different
const calculateSimilarity = (str1, str2) => {
    const distance = levenshteinDistance(str1, str2);
    const maxLen = Math.max(str1.length, str2.length);
    if (maxLen === 0) return 100;
    return Math.round(((maxLen - distance) / maxLen) * 100);
};

// Check phonetic similarity (key letters match even if order differs)
// Example: "vikash" contains "k", "s", "h" like "bkash"
const getPhoneticScore = (str1, str2) => {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    // If very short, don't use phonetic (too risky)
    if (s1.length < 3 || s2.length < 3) return 0;

    // Extract key consonants (skip vowels a, e, i, o, u)
    const extractConsonants = (str) => {
        return str.replace(/[aeiou]/g, '').split('');
    };

    const cons1 = extractConsonants(s1);
    const cons2 = extractConsonants(s2);

    // Count matching consonants
    let matches = 0;
    for (const c of cons2) {
        if (cons1.includes(c)) {
            matches++;
        }
    }

    // Score based on consonant match percentage
    const maxCons = Math.max(cons1.length, cons2.length);
    if (maxCons === 0) return 0;

    return Math.round((matches / maxCons) * 100);
};

// Check if words sound similar (common voice recognition errors)
// Example: "vikash" could be misheard "bkash", "pekash", "ekash"
const getSoundAlikeScore = (str1, str2) => {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    // If endings match, likely the same word
    if (s1.length > 2 && s2.length > 2) {
        const end1 = s1.slice(-3);
        const end2 = s2.slice(-3);
        if (end1 === end2) return 75; // Strong indicator
    }

    // If have same length and mostly same letters, might be voice error
    if (Math.abs(s1.length - s2.length) <= 1) {
        const charMatches = [...s1].filter((c) => s2.includes(c)).length;
        const avgLen = (s1.length + s2.length) / 2;
        return Math.round((charMatches / avgLen) * 100);
    }

    return 0;
};

// Fuzzy match a voice input word against items (categories/accounts)
// Returns best matches with confidence scores
const fuzzyMatchVoiceInput = (voiceWord, items, minConfidence = 70) => {
    if (!voiceWord || !Array.isArray(items) || items.length === 0) {
        return [];
    }

    const matches = items.map((item) => {
        const itemName = item.name || '';
        let score = 0;

        // 1. Exact match (case-insensitive) - highest priority
        if (voiceWord.toLowerCase() === itemName.toLowerCase()) {
            score = 100;
        }
        // 2. Starts with match (whole item name starts with voice word)
        else if (itemName.toLowerCase().startsWith(voiceWord.toLowerCase())) {
            score = 90;
        }
        // 3. Contains match — only if voice word matches a WHOLE word in the item name
        //    e.g., "cash" should NOT match "cashback" (substring of a word)
        //    but "food" should match "Food & Drinks" (standalone word)
        else if (
            new RegExp(
                `\\b${voiceWord.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
                'i'
            ).test(itemName)
        ) {
            score = 85;
        }
        // 4. Levenshtein fuzzy match (character-level similarity)
        else {
            const levenScore = calculateSimilarity(voiceWord, itemName);

            // 5. Phonetic score (consonant matching for voice variations)
            const phoneticScore = getPhoneticScore(voiceWord, itemName);

            // 6. Sound-alike score (common voice recognition errors)
            const soundAlikeScore = getSoundAlikeScore(voiceWord, itemName);

            // Combine scores: levenshtein is primary, others can boost it
            score = levenScore;

            // If phonetic match is strong and levenshtein is medium, boost score
            if (phoneticScore > 60 && levenScore > 50) {
                score = Math.max(score, Math.round((levenScore + phoneticScore) / 2));
            }

            // If sound-alike match is strong, boost
            if (soundAlikeScore > 70) {
                score = Math.max(score, soundAlikeScore);
            }
        }

        return {
            ...item,
            confidence: score,
            matchedWord: voiceWord,
        };
    });

    // Return matches above threshold, sorted by confidence descending
    return matches
        .filter((m) => m.confidence >= minConfidence)
        .sort((a, b) => b.confidence - a.confidence);
};

// Fuzzy match entire voice text against items
// Returns best single match with confidence
const fuzzyMatchVoiceText = (voiceText, items, minConfidence = 70) => {
    if (!voiceText || !Array.isArray(items) || items.length === 0) {
        return null;
    }

    const words = voiceText.toLowerCase().split(/\s+/);
    let bestMatch = null;
    let highestConfidence = minConfidence - 1;

    for (const word of words) {
        if (word.length < 2) continue;

        for (const item of items) {
            const itemName = item.name || '';
            let score = 0;

            // Exact match
            if (word === itemName.toLowerCase()) {
                score = 100;
            }
            // Starts with
            else if (itemName.toLowerCase().startsWith(word)) {
                score = 90;
            }
            // Contains — only whole word match (e.g., "cash" should NOT match "cashback")
            else if (
                new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(
                    itemName
                )
            ) {
                score = 80;
            }
            // Fuzzy
            else {
                score = calculateSimilarity(word, itemName);
            }

            if (score > highestConfidence) {
                highestConfidence = score;
                bestMatch = { ...item, confidence: score };
            }
        }
    }

    return bestMatch && bestMatch.confidence >= minConfidence ? bestMatch : null;
};

/**
 * Detect if text is in Bengali or English
 * Uses hybrid approach:
 * 1. First checks for Bengali Unicode characters (0x0980-0x09FF)
 * 2. Falls back to character count comparison
 * @param {string} text - Text to detect
 * @param {string} preferredLanguage - User's selected language ('en' or 'bn') - used as fallback
 * @returns {string} - Detected language ('en' or 'bn')
 */
const detectLanguageFromText = (text, preferredLanguage = 'en') => {
    if (!text || text.trim() === '') {
        return preferredLanguage;
    }

    // Bengali Unicode range: 0x0980 to 0x09FF
    const bengaliRegex = /[\u0980-\u09FF]/g;
    const bengaliMatches = text.match(bengaliRegex);

    // English/Latin range
    const englishRegex = /[a-zA-Z]/g;
    const englishMatches = text.match(englishRegex);

    const bengaliCharCount = bengaliMatches ? bengaliMatches.length : 0;
    const englishCharCount = englishMatches ? englishMatches.length : 0;

    // If clear Bengali characters found (even 1), consider it Bengali
    if (bengaliCharCount > 0 && bengaliCharCount >= englishCharCount) {
        return 'bn';
    }

    // If clear English characters found without Bengali
    if (englishCharCount > bengaliCharCount) {
        return 'en';
    }

    // If no clear detection (numbers/special chars only), use user's preference
    return preferredLanguage;
};

// Convert word numbers to digits (handles "one", "two", "seven", "lakh", "crore", etc.)
const wordNumberToDigit = (text) => {
    if (!text) return text;

    const englishWordNumbers = {
        zero: 0,
        one: 1,
        two: 2,
        three: 3,
        four: 4,
        five: 5,
        six: 6,
        seven: 7,
        eight: 8,
        nine: 9,
        ten: 10,
        eleven: 11,
        twelve: 12,
        thirteen: 13,
        fourteen: 14,
        fifteen: 15,
        sixteen: 16,
        seventeen: 17,
        eighteen: 18,
        nineteen: 19,
        twenty: 20,
        thirty: 30,
        forty: 40,
        fifty: 50,
        sixty: 60,
        seventy: 70,
        eighty: 80,
        ninety: 90,
    };

    const englishMultipliers = {
        hundred: 100,
        thousand: 1000,
        lakh: 100000,
        lac: 100000,
        million: 1000000,
        crore: 10000000,
    };

    const bengaliWordNumbers = {
        শূন্য: 0,
        শূণ্য: 0,
        এক: 1,
        দুই: 2,
        তিন: 3,
        চার: 4,
        পাঁচ: 5,
        ছয়: 6,
        সাত: 7,
        আট: 8,
        নয়: 9,
        দশ: 10,
        এগারো: 11,
        বারো: 12,
        তেরো: 13,
        চৌদ্দ: 14,
        পনের: 15,
        ষোল: 16,
        সতের: 17,
        আঠার: 18,
        উনিশ: 19,
        বিশ: 20,
        ত্রিশ: 30,
        চল্লিশ: 40,
        পঞ্চাশ: 50,
        ষাট: 60,
        সত্তর: 70,
        আশি: 80,
        নব্বই: 90,
    };

    const bengaliMultipliers = {
        শত: 100,
        হাজার: 1000,
        লক্ষ: 100000,
        লাখ: 100000,
        কোটি: 10000000,
    };

    let result = text.toLowerCase();

    // --- English: word-number + multiplier-word → product ---
    // e.g., "one lakh" → 100000, "five hundred" → 500
    const enNumWords = Object.keys(englishWordNumbers).join('|');
    const enMultWords = Object.keys(englishMultipliers).join('|');
    result = result.replace(
        new RegExp(`\\b(${enNumWords})\\s+(${enMultWords})\\b`, 'gi'),
        (_, numW, mulW) =>
            String(englishWordNumbers[numW.toLowerCase()] * englishMultipliers[mulW.toLowerCase()])
    );

    // --- English: digit + multiplier-word → product ---
    // e.g., "1 lakh" → 100000, "18 thousand" → 18000
    result = result.replace(new RegExp(`(\\d+)\\s+(${enMultWords})\\b`, 'gi'), (_, num, mulW) =>
        String(parseInt(num) * englishMultipliers[mulW.toLowerCase()])
    );

    // --- English: convert remaining standalone word numbers to digits ---
    for (const [word, digit] of Object.entries(englishWordNumbers)) {
        result = result.replace(new RegExp(`\\b${word}\\b`, 'gi'), String(digit));
    }
    // Convert standalone multiplier words (rare edge case: just "lakh" alone)
    for (const [word, digit] of Object.entries(englishMultipliers)) {
        result = result.replace(new RegExp(`\\b${word}\\b`, 'gi'), String(digit));
    }

    // --- Bengali: word-number + multiplier-word → product ---
    // e.g., "এক লক্ষ" → 100000, "আট হাজার" → 8000
    const bnNumWords = Object.keys(bengaliWordNumbers).join('|');
    const bnMulWords = Object.keys(bengaliMultipliers).join('|');
    result = result.replace(
        new RegExp(`(${bnNumWords})\\s+(${bnMulWords})`, 'g'),
        (_, numW, mulW) => String(bengaliWordNumbers[numW] * bengaliMultipliers[mulW])
    );

    // --- Bengali: digit + multiplier-word → product ---
    // e.g., "1 লক্ষ" → 100000, "18 হাজার" → 18000
    result = result.replace(new RegExp(`(\\d+)\\s+(${bnMulWords})`, 'g'), (_, num, mulW) =>
        String(parseInt(num) * bengaliMultipliers[mulW])
    );

    // --- Bengali: convert remaining standalone word numbers ---
    for (const [word, digit] of Object.entries(bengaliWordNumbers)) {
        result = result.replace(new RegExp(`${word}`, 'g'), String(digit));
    }
    for (const [word, digit] of Object.entries(bengaliMultipliers)) {
        result = result.replace(new RegExp(`${word}`, 'g'), String(digit));
    }

    return result;
};

export {
    formatName,
    getDateTimeInTimezone,
    amountDisplay,
    formatAmountInput,
    dateDisplay,
    normalizeMonthParam,
    getUserTimezone,
    englishToBengaliNumber,
    bengaliToEnglishNumber,
    parseAmount,
    isDarkModeOn,
    getMonthName,
    numberLanguageWise,
    shortMonthConverter,
    levenshteinDistance,
    calculateSimilarity,
    fuzzyMatchVoiceInput,
    fuzzyMatchVoiceText,
    detectLanguageFromText,
    wordNumberToDigit,
};
